import { ReactElement } from 'react';
import { adminLayoutWrap } from '@middlewares';
import { Flag } from '@utils';
import {
  ActiveAccessPage,
  AddPersonalApiTokenPage,
  AddReportPage,
  AdminBoxPage,
  CatalogPageV2,
  EditReportPage,
  HomePage,
  InstallConnectorPage,
  PersonalApiTokensPage,
  ReportsPage,
  SettingsPage,
} from '@pages';
import { ConnectorsPage } from '@pages/ConnectorsPage/ConnectorsPage';
import { EditConnectorPage } from '@pages/EditConnectorPage';
import { SearchPage } from '@pages/SearchPage';
import { ActivityPage } from './Activity/Audit/ActivityPage';
import { routes as webhookRoutes } from './Webhooks/routes';
import { routes as onboardingRoutes } from '@Onboarding/routes';
import { routes as integrationsRoutes } from '@Integrations/routes';
import { routes as accessFlowsRoutes } from '@AccessFlows/routes';
import { routes as identitiesRoutes } from '@Identities/routes';
import { routes as auditEventsRoutes } from '@AuditEvents/routes';
import { routes as inventoryRoutes } from '@Inventory/routes';
import { routes as standingAccessRoutes } from '@StandingAccess/routes';
import { routes as systemLogRoutes } from '@SystemLog/routes';
import GroupsPage from '@Groups/pages/GroupsPage';
import { AccessGraphPage } from './AccessGraph/pages/AccessGraphPage';
import { AccessAlertsPage } from './AccessAlerts/pages/AccessAlertsPage';
import { Permission } from '@common/utils/admin_permissions';

export interface PageRoute {
  label: string;
  path: string;
  page: ReactElement;
}

export const routes = {
  Dashboard: {
    label: 'Home',
    path: '/',
    page: adminLayoutWrap({
      children: <HomePage />,
      permissions: [Permission.ADMIN_APP_READ],
    }),
  },
  Search: {
    label: 'Search',
    path: '/search',
    page: adminLayoutWrap({
      children: <SearchPage />,
      permissions: [Permission.IDENTITIES_READ, Permission.ACCESS_FLOWS_READ, Permission.INTEGRATIONS_READ],
    }),
  },
  Activity: {
    label: 'Audit',
    path: '/activity',
    page: adminLayoutWrap({
      children: <ActivityPage />,
      permissions: [Permission.ACTIVITY_READ],
    }),
  },
  Groups: {
    label: 'Groups',
    path: '/groups',
    page: adminLayoutWrap({
      children: <GroupsPage />,
      permissions: [Permission.IDENTITIES_READ],
    }),
  },
  EditConnector: {
    label: 'Edit Connector',
    path: '/connectors/edit/:id',
    page: adminLayoutWrap({
      children: <EditConnectorPage />,
      permissions: [Permission.CONNECTORS_WRITE],
    }),
  },
  InstallConnector: {
    label: 'Add Connector',
    path: '/connectors/install',
    page: adminLayoutWrap({
      children: <InstallConnectorPage />,
      permissions: [Permission.CONNECTORS_WRITE],
    }),
  },
  Connectors: {
    label: 'Connectors',
    path: '/connectors',
    page: adminLayoutWrap({
      children: <ConnectorsPage />,
      permissions: [Permission.CONNECTORS_READ],
    }),
  },
  PersonalApiTokens: {
    label: 'Personal API Tokens',
    path: '/personal-api-tokens',
    page: adminLayoutWrap({
      children: <PersonalApiTokensPage />,
      flag: Flag.NEW_UI_PERSONAL_TOKENS,
      permissions: [Permission.PERSONAL_API_TOKEN_WRITE],
    }),
  },
  AddPersonalApiToken: {
    label: 'Add Personal API Token',
    path: '/personal-api-tokens/add',
    page: adminLayoutWrap({
      children: <AddPersonalApiTokenPage />,
      flag: Flag.NEW_UI_PERSONAL_TOKENS,
      permissions: [Permission.PERSONAL_API_TOKEN_WRITE],
    }),
  },
  Settings: {
    label: 'Settings',
    path: '/settings',
    page: adminLayoutWrap({
      children: <SettingsPage />,
      permissions: [Permission.SETTINGS_READ],
    }),
  },
  Catalog: {
    label: 'Catalog',
    path: '/catalog',
    page: adminLayoutWrap({
      children: <CatalogPageV2 />,
      permissions: [Permission.INTEGRATIONS_READ],
    }),
  },
  Reports: {
    label: 'Reports',
    path: '/reports',
    page: adminLayoutWrap({
      children: <ReportsPage />,
      permissions: [Permission.REPORTS_READ],
    }),
  },
  AddReport: {
    label: 'Add Report',
    path: '/reports/add',
    page: adminLayoutWrap({
      children: <AddReportPage />,
      permissions: [Permission.REPORTS_WRITE],
    }),
  },
  EditReport: {
    label: 'Edit Report',
    path: '/reports/edit/:id',
    page: adminLayoutWrap({
      children: <EditReportPage />,
      permissions: [Permission.REPORTS_WRITE],
    }),
  },
  ActiveAccess: {
    label: 'Active Access',
    path: '/active-access',
    page: adminLayoutWrap({
      children: <ActiveAccessPage />,
      flag: Flag.NEW_UI_ACTIVE_ACCESS,
      permissions: [Permission.ACCESS_FLOWS_READ],
    }),
  },
  AccessGraph: {
    label: 'Access Graph',
    path: '/access-graph',
    page: adminLayoutWrap({
      children: <AccessGraphPage />,
      flag: Flag.APONO_ACCESS_GRAPH,
      permissions: [Permission.ACCESS_GRAPH_READ],
    }),
  },
  AccessAlerts: {
    label: 'Anomalies',
    path: '/access-anomalies',
    page: adminLayoutWrap({
      children: <AccessAlertsPage />,
      flag: Flag.ACCESS_ANOMALIES,
      permissions: [Permission.ANOMALIES_READ],
    }),
  },
  AdminBoxPage: {
    label: 'Admin Box',
    path: '/admin-box',
    page: adminLayoutWrap({ children: <AdminBoxPage /> }),
  },
  ...onboardingRoutes,
  ...accessFlowsRoutes,
  ...webhookRoutes,
  ...integrationsRoutes,
  ...identitiesRoutes,
  ...auditEventsRoutes,
  ...inventoryRoutes,
  ...standingAccessRoutes,
  ...systemLogRoutes,
};
